import React, { useState, useEffect } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserCreate, UserUpdate, UserRole, listRoles } from '@/app/services/userManagementService';
import { UserWithSites } from '@/app/services/userManagementService';
import { toast } from 'sonner';

// Define form schema using zod
const formSchema = z.object({
  username: z.string().min(3, 'Username must be at least 3 characters'),
  email: z.string().email('Invalid email address'),
  full_name: z.string().min(1, 'Full name is required'),
  // Allow password to be either a valid password (min 6 chars) OR an empty string (for edit mode)
  password: z.string().min(6, 'Password must be at least 6 characters').or(z.literal('')).optional(),
  role_id: z.number().positive('Role is required'),
  is_active: z.boolean().default(true),
});

type FormValues = z.infer<typeof formSchema>;

interface UserManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: UserCreate | UserUpdate) => void;
  defaultValues?: Partial<UserWithSites>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

const UserManagementModal: React.FC<UserManagementModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
}) => {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: defaultValues?.username || '',
      email: defaultValues?.email || '',
      full_name: defaultValues?.full_name || defaultValues?.name || '',
      password: '',
      role_id: defaultValues?.role?.id || 0,
      is_active: defaultValues?.is_active ?? true,
    },
  });

  // Reset form values when defaultValues change or modal opens
  useEffect(() => {
    if (open && defaultValues) {
      form.reset({
        username: defaultValues?.username || '',
        email: defaultValues?.email || '',
        full_name: defaultValues?.full_name || defaultValues?.name || '',
        password: '',
        role_id: defaultValues?.role?.id || 0,
        is_active: defaultValues?.is_active ?? true,
      });
    } else if (open && !defaultValues) {
      // Reset to empty values for create mode
      form.reset({
        username: '',
        email: '',
        full_name: '',
        password: '',
        role_id: 0,
        is_active: true,
      });
    }
  }, [open, defaultValues, form]);

  // Load available roles
  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoadingRoles(true);
        const fetchedRoles = await listRoles();
        setRoles(fetchedRoles);
      } catch (error) {
        toast.error('Failed to load roles');
        console.error('Error loading roles:', error);
      } finally {
        setLoadingRoles(false);
      }
    };

    if (open) {
      loadRoles();
    }
  }, [open]);

  const handleSubmit = (values: FormValues): void => {
    if (mode === 'edit') {
      // In edit mode, only send editable fields (no password)
      const updateData: UserUpdate = {
        email: values.email,
        full_name: values.full_name,
        role_id: values.role_id,
        is_active: values.is_active,
      };

      onSubmit(updateData);
    } else {
      // In create mode, password is required
      if (!values.password || values.password.length === 0) {
        form.setError('password', { message: 'Password is required for new users' });
        return;
      }
      onSubmit(values as UserCreate);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create New User' : 'Edit User'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter username" disabled={mode === 'edit'} readOnly={mode === 'edit'} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="user@example.com" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter full name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {mode === 'create' && (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" placeholder="Enter password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="role_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select 
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    value={field.value?.toString()}
                    disabled={loadingRoles}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={loadingRoles ? "Loading roles..." : "Select a role"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.id} value={role.id.toString()}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isLoading || loadingRoles}>
                {isLoading ? 'Processing...' : mode === 'create' ? 'Create User' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UserManagementModal;